import { useState } from 'react';
import type { FormField, PaymentMethod } from '../../types';
import { availableMethods, resolveFrequency, resolveSelectedMethod, type PaymentSelection } from '../../payment-methods';
import { FormFieldWrapper } from './FormFieldWrapper';

export type { PaymentSelection };

const METHOD_LABELS: Record<PaymentMethod, string> = {
  card: 'Card',
  direct_debit: 'Direct Debit',
};

interface Props {
  field: FormField;
  value: unknown;
  onChange: (v: PaymentSelection) => void;
  error?: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = { gbp: '£', usd: '$', eur: '€' };

export function PaymentField({ field, value, onChange, error }: Props) {
  const config = field.payment;
  const selection = (value as PaymentSelection | undefined) ?? {};
  const symbol = config ? (CURRENCY_SYMBOLS[config.currency] ?? '') : '';

  // A gift handle field whose gift handles were all dropped when the form was
  // served has nothing to pick, so it opens straight on the amount box.
  const noGiftHandlesLeft =
    config?.amountMode === 'donor_choice' && config.presetSource === 'gift_handles' && !config.giftHandles?.length;
  const [custom, setCustom] = useState(noGiftHandlesLeft);
  // The builder preview re-renders with a new config as the author edits, so
  // follow the gift handles appearing or disappearing, not just the first render.
  // Adjusted during render (React's pattern for state derived from props)
  // rather than in an effect, which would paint the stale state first.
  const [noneLeftBefore, setNoneLeftBefore] = useState(noGiftHandlesLeft);
  if (noneLeftBefore !== noGiftHandlesLeft) {
    setNoneLeftBefore(noGiftHandlesLeft);
    setCustom(noGiftHandlesLeft);
  }

  if (!config) return null;

  const frequency = resolveFrequency(config, selection);
  const methods = availableMethods(config, selection);
  const selectedMethod = resolveSelectedMethod(config, selection);

  // Any amount not picked from a gift handle clears the gift handle it replaced.
  const setAmountMinor = (amountMinor: number | undefined) =>
    onChange({ ...selection, amountMinor, giftHandleId: undefined, frequency });
  // Switching to one-off can take Direct Debit off the table (a one-off Direct
  // Debit is a different GoCardless product), so the method is re-resolved in the
  // same change rather than left pointing at an option that is no longer shown.
  const setFrequency = (f: 'one_off' | 'recurring') => {
    const next = { ...selection, frequency: f };
    onChange({ ...next, method: resolveSelectedMethod(config, next) });
  };
  const setMethod = (method: PaymentMethod) => onChange({ ...selection, frequency, method });

  const presets = config.amountMode === 'donor_choice' ? config.presetAmountsMinor ?? [] : [];
  // Gift handles replace the amount buttons when the author chose them and at
  // least one survived resolution; otherwise the plain buttons (or just "Other")
  // keep the form usable.
  const giftHandles =
    config.amountMode === 'donor_choice' && config.presetSource === 'gift_handles' ? config.giftHandles ?? [] : [];
  const useGiftHandles = config.presetSource === 'gift_handles';
  const formatAmount = (minor: number) => `${symbol}${(minor / 100).toFixed(minor % 100 === 0 ? 0 : 2)}`;

  return (
    <FormFieldWrapper field={field} error={error}>
      {config.amountMode === 'fixed' ? (
        <p className="payment-fixed-amount">
          {symbol}
          {((config.fixedAmountMinor ?? 0) / 100).toFixed(2)}
        </p>
      ) : (
        <div className={useGiftHandles ? 'gift-handles' : 'amount-presets'}>
          {useGiftHandles && giftHandles.map((handle) => {
            const selected = selection.giftHandleId === handle.giftHandleId && !custom;
            return (
              <button
                key={handle.giftHandleId}
                type="button"
                aria-pressed={selected}
                className={`gift-handle-option ${selected ? 'active' : ''}`}
                onClick={() => {
                  setCustom(false);
                  onChange({ ...selection, amountMinor: handle.amountMinor, giftHandleId: handle.giftHandleId, frequency });
                }}
              >
                <span className="gift-handle-amount">{formatAmount(handle.amountMinor)}</span>
                <span className="gift-handle-title">{handle.title}</span>
                <span className="gift-handle-description">{handle.description}</span>
              </button>
            );
          })}
          {!useGiftHandles && presets.map((minor) => (
            <button
              key={minor}
              type="button"
              className={`preset-btn ${selection.amountMinor === minor && !custom ? 'active' : ''}`}
              onClick={() => {
                setCustom(false);
                setAmountMinor(minor);
              }}
            >
              {symbol}
              {(minor / 100).toFixed(0)}
            </button>
          ))}
          <button
            type="button"
            className={`preset-btn ${custom ? 'active' : ''}`}
            onClick={() => {
              setCustom(true);
              setAmountMinor(undefined);
            }}
          >
            Other
          </button>
          {custom && (
            <input
              type="number"
              min="0.01"
              step="0.01"
              placeholder="Enter amount"
              onChange={(e) => {
                const major = parseFloat(e.target.value);
                setAmountMinor(Number.isNaN(major) ? undefined : Math.round(major * 100));
              }}
            />
          )}
        </div>
      )}

      {config.frequency === 'donor_choice' && (
        <div className="payment-frequency">
          <label>
            <input
              type="radio"
              name={`${field.fieldId}-frequency`}
              checked={frequency === 'one_off'}
              onChange={() => setFrequency('one_off')}
            />
            One-off
          </label>
          <label>
            <input
              type="radio"
              name={`${field.fieldId}-frequency`}
              checked={frequency === 'recurring'}
              onChange={() => setFrequency('recurring')}
            />
            {config.recurringInterval === 'year' ? 'Yearly' : 'Monthly'}
          </label>
        </div>
      )}

      {/* A radio group of one is noise, so the choice appears only when there is
          genuinely more than one way to pay for THIS selection. */}
      {methods.length > 1 && (
        <div className="payment-method">
          {methods.map((method) => (
            <label key={method}>
              <input
                type="radio"
                name={`${field.fieldId}-method`}
                checked={selectedMethod === method}
                onChange={() => setMethod(method)}
              />
              {METHOD_LABELS[method]}
            </label>
          ))}
        </div>
      )}
    </FormFieldWrapper>
  );
}
